import { VERSION } from "../../constants";
import { IDBManager } from "../../utils/idb";
import { fetchAsset } from "../../utils/network";

// Icons are cached for 90 days. The IDBManager deletes the
// records once they expire.
const ICON_CACHE_TTL = 1000 * 60 * 60 * 24 * 90;

// Asset requests are stored/memoized to prevent duplicate
// in-flight requests.
const fetches = new Map<string, Promise<string>>();

// Persist an instance of the store
const store = new IDBManager({ name: "icons" });

/**
 * Gets icon xml for use in the template. It first checks
 * cache, then makes a network request if necessay.
 */
export async function getIcon(key: string) {
  const { set, name } = parseIconKey(key);

  // Check cache first
  const cacheKey = `${set}:${name}:${VERSION}`;
  const cached = await store.get<string>(cacheKey);

  // Use the cached value if possible
  if (cached) {
    return cached.value;
  }

  // Memoize the request
  let promise: Promise<string>;

  const pathname = `/icons/${set}/${name}.svg`;

  if (fetches.has(pathname)) {
    promise = fetches.get(pathname)!;
  } else {
    promise = fetchAsset(pathname);
    fetches.set(pathname, promise);
  }

  return promise.then((body) => {
    // Save the icon to cache
    store
      .put(cacheKey, {
        expires: Date.now() + ICON_CACHE_TTL,
        value: body,
      })
      .then(() => {
        // Clean-up the fetch
        fetches.delete(pathname);
      })
      .catch(() => undefined);

    return body;
  });
}

/**
 * Parses an icon key (the `icon` attribute) and returns
 * information needed to form a request.
 */
export function parseIconKey(key: string) {
  const { set = "regular", name } =
    key.match(/((?<set>.+):)?(?<name>.+)/)?.groups ?? {};

  return { set, name };
}
